import React from 'react';
import { Helmet } from 'react-helmet';
import { Button, Calendar, Checkbox, Collapse, Divider, Layout, notification, Switch } from 'antd';

import LeftBar from '../../components/common/NavBar';
import ScheduleCalendar from './ScheduleCalendar';
import { RouteComponentProps } from 'react-router-dom';
import {
    getDoctorSelectorList,
    getMyClinics,
    getSelectedClinicId,
    setSelectedClinicId,
} from '../../store/idbHandler';
import { captureError } from '../captureError';
import { GO_TO_NEW_APPOINTMENT, GO_TO_SCHEDULE } from '../../routes/paths';
import AntSelect from '../../components/prescriptionPad/rightPanel/workspace/padV2/AntSelect';
import { capitalizeFirstLetter, getDayWord } from '../utils';
import ClinicSelector from '../../components/common/ClinicSelector';
import NewHeaderComponent from '../../components/common/NewHeaderComponent';
import { track } from '../../tracking';
import { toggleGlobalSearch } from '../../store/actions/session';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { GlobalState } from '../../store/reducers';
import { Actions } from '../../store/actions';
import { MyDoc } from '../../api/fetchMyProfile';
import ResourceAvatar from './ResourceAvatar';
import moment, { Moment } from 'moment';
import TableWithDragableRows, {
    DragHandle,
} from '../../components/prescriptionPad/rightPanel/workspace/padV2/TableWithDragableRows';
import { arrayMoveImmutable } from 'array-move';
import {
    getScheduleDoctorList,
    getScheduleSidebarCollapsed,
    setScheduleDoctorList,
    setScheduleSidebarCollapsed,
} from '../../store/localstorage';
import tr from 'date-fns/locale/tr';

type RouterProps = {
    cId: string;
};

interface Props extends RouteComponentProps<RouterProps> {
    dispatch: ThunkDispatch<GlobalState, null, Actions>;
}

type State = {
    timeView: 'day' | 'week' | 'month';
    resources: { id: string; title: string; extendedProps: { doc: MyDoc }; selected: boolean }[];
    isFreeSlotOnly?: boolean;
    date: Moment;
    isSiderCollapsed?: boolean;
};

class SchedulePage extends React.PureComponent<Props, State> {
    defaultSelectedDoctorsList = new Set(getScheduleDoctorList());

    state: State = {
        timeView: 'day',
        resources: getDoctorSelectorList().map((doc, index) => ({
            id: doc.id,
            title: 'Dr ' + doc.personal.name.f + ' ' + doc.personal.name.l,
            extendedProps: { doc },
            selected: this.defaultSelectedDoctorsList.size
                ? this.defaultSelectedDoctorsList.has(doc.id)
                : index < 5,
        })),
        date: moment().startOf('day'),
        isFreeSlotOnly: false,
        isSiderCollapsed: getScheduleSidebarCollapsed(),
    };

    rowKeyFromId = (id: string, index?: number): string => {
        return id + '-' + index;
    };

    findIndexForRecordKey = (key: string): number => {
        const { resources } = this.state;
        return resources.findIndex((e, index) => this.rowKeyFromId(e.id, index) === key);
    };

    onDrag = ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }): void => {
        const { resources } = this.state;
        if (oldIndex !== newIndex) {
            const newResources = arrayMoveImmutable([...resources], oldIndex, newIndex).filter(
                (phdata) => !!phdata,
            );
            this.setState({
                resources: newResources,
            });
        }
        track('REORDER_DOCTORS', {
            oldIndex: oldIndex.toString(),
            newIndex: newIndex.toString(),
            doctorsCount: resources.length.toString(),
        });
    };

    onTimeViewChange = (timeView: 'day' | 'week' | 'month'): void => {
        this.setState({
            timeView,
        });
        track('TIME_VIEW_CHANGE', { timeView });
    };

    componentDidMount(): void {
        if (!this.checkForValidUrlParams()) {
            return;
        }
    }

    componentDidUpdate(prevProps: Readonly<Props>): void {
        if (prevProps.match.params.cId !== this.props.match.params.cId) {
            if (!this.checkForValidUrlParams()) {
                return;
            }
        }
    }

    checkForValidUrlParams = (): boolean => {
        const clinics = getMyClinics();
        if (clinics?.find((c) => c._id === this.props.match.params.cId)) {
        } else {
            const cId =
                clinics?.find((c) => c._id === this.props.match.params.cId)?._id ||
                getSelectedClinicId() ||
                '';
            if (!cId) {
                captureError(new Error("Default client not found in user's clinics"));
                return false;
            }
            const t = GO_TO_SCHEDULE(cId);
            this.props.history.replace(t);
            return false;
        }

        return true;
    };

    onResourceCheckChange = (id: string): void => {
        const { resources } = this.state;
        if (resources.length <= 1) {
            return;
        }

        const t = resources.find((r) => r.id === id);
        if (!t) {
            return;
        }
        t.selected = !t.selected;

        track('DOCTOR_SELECT', {
            doctorId: id,
            selected: t.selected.toString(),
        });

        if (!resources.find((t) => t.selected)) {
            // undo the mutation
            t.selected = !t.selected;
            notification.error({
                message: 'Error',
                description: 'At least one doctor should be selected',
            });
            return;
        }
        this.setState({
            resources: [...resources],
        });

        setScheduleDoctorList(resources.filter((t) => t.selected).map((r) => r.id));
    };

    toggleFreeSlotOnly = (checked: boolean): void => {
        this.setState({
            isFreeSlotOnly: checked,
        });
        track('FREE_SLOT_ONLY', { checked: checked.toString() });
    };

    onDateChange = (date: Moment): void => {
        this.setState({
            date,
        });
        track('DATE_CHANGE', { date: date.format('YYYY-MM-DD') });
    };

    toggleSider = (): void => {
        const { isSiderCollapsed } = this.state;
        this.setState({
            isSiderCollapsed: !isSiderCollapsed,
        });
        setScheduleSidebarCollapsed(!isSiderCollapsed);
        track('TOGGLE_SCHEDULE_SIDEBAR', { isSiderCollapsed: (!isSiderCollapsed).toString() });
    };

    onGoToQueue = (): void => {
        this.props.history.push(GO_TO_NEW_APPOINTMENT(this.props.match.params.cId));
        track('GO_TO_QUEUE', { cId: this.props.match.params.cId });
    };

    render(): JSX.Element {
        const { resources, timeView, isFreeSlotOnly, date, isSiderCollapsed } = this.state;

        return (
            <div key={this.props.match.params.cId} className="flex flex-col w-full h-full bg-white font-lato overflow-hidden h-full select-none">
                <Helmet>
                    <meta charSet="utf-8" />
                    <title>EKA - Schedule</title>
                </Helmet>

                <div className="grid grid-cols-queueGrid h-full min-w-840 overflow-x-auto overflow-y-hidden undefined">
                    <LeftBar />
                    <div className="flex col-span-11 flex-col overflow-hidden h-full">
                        <NewHeaderComponent
                            title="Schedule"
                            filterComponent={
                                <div
                                    className="flex flex-1 items-center justify-between"
                                    style={{ marginLeft: 194 }}
                                >
                                    <div className="flex space-x-8">
                                        <AntSelect
                                            className="bg-darwin-neutral-50 text-darwin-neutral-600"
                                            showArrow={true}
                                            customDropdownWidth={100}
                                            allowClear={false}
                                            optionFilterProp="children"
                                            options={['day', 'week', 'month']}
                                            setValue={(option) => {
                                                this.onTimeViewChange(
                                                    option as 'day' | 'week' | 'month',
                                                );
                                            }}
                                            filterOption={true}
                                            value={this.state.timeView}
                                            getValue={(o) => o}
                                            getLabelToCompare={(o) => capitalizeFirstLetter(o)}
                                        />
                                        <ClinicSelector
                                            onClinicSelect={(selectedClinicId) => {
                                                setSelectedClinicId(selectedClinicId);
                                                this.props.history.replace(
                                                    GO_TO_SCHEDULE(selectedClinicId),
                                                );
                                            }}
                                        />
                                        <div className="dr-flex dr-bg-darwin-touch-neutral-50 dr-p-2 dr-rounded-8">
                                            <div
                                                className="p-8 dr-cursor-pointer"
                                                onClick={this.onGoToQueue}
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 512 512"
                                                    width="14"
                                                    fill="currentColor"
                                                >
                                                    <path d="M40 48C26.7 48 16 58.7 16 72l0 48c0 13.3 10.7 24 24 24l48 0c13.3 0 24-10.7 24-24l0-48c0-13.3-10.7-24-24-24L40 48zM192 64c-17.7 0-32 14.3-32 32s14.3 32 32 32l288 0c17.7 0 32-14.3 32-32s-14.3-32-32-32L192 64zm0 160c-17.7 0-32 14.3-32 32s14.3 32 32 32l288 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-288 0zm0 160c-17.7 0-32 14.3-32 32s14.3 32 32 32l288 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-288 0zM16 232l0 48c0 13.3 10.7 24 24 24l48 0c13.3 0 24-10.7 24-24l0-48c0-13.3-10.7-24-24-24l-48 0c-13.3 0-24 10.7-24 24zM40 368c-13.3 0-24 10.7-24 24l0 48c0 13.3 10.7 24 24 24l48 0c13.3 0 24-10.7 24-24l0-48c0-13.3-10.7-24-24-24l-48 0z"></path>
                                                </svg>
                                            </div>
                                            <div
                                                className="p-8 bg-white rounded-6 dr-cursor-pointer"
                                                style={{
                                                    boxShadow: 'rgba(0, 0, 0, 0.12) 0px 0px 8px;',
                                                }}
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 448 512"
                                                    fill="currentColor"
                                                    width="14"
                                                >
                                                    <path
                                                        d="M152 24c0-13.3-10.7-24-24-24s-24 10.7-24 24l0 40L64 64C28.7 64 0 92.7 0 128l0 16 0 48L0 448c0 35.3 28.7 64 64 64l320 0c35.3 0 64-28.7 64-64l0-256 0-48 0-16c0-35.3-28.7-64-64-64l-40 0 0-40c0-13.3-10.7-24-24-24s-24 10.7-24 24l0 40L152 64l0-40zM48 192l352 0 0 256c0 8.8-7.2 16-16 16L64 464c-8.8 0-16-7.2-16-16l0-256z"
                                                        fill="#215FFF"
                                                    ></path>
                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-16">
                                        <div
                                            className="rounded-8 flex items-center space-x-8 py-6 px-12 border border-darwin-neutral_new-200 cursor-pointer"
                                            style={{ width: 180 }}
                                            onClick={() => {
                                                track('GLOBAL_SEARCH', { where: 'schedule' });
                                                this.props.dispatch(toggleGlobalSearch(true));
                                            }}
                                        >
                                            <svg
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M19.75 18.7188C20.0625 19.0312 20.0625 19.5 19.75 19.7812C19.625 19.9375 19.4375 20 19.25 20C19.0312 20 18.8438 19.9375 18.6875 19.7812L14.5 15.5938C13.375 16.5 11.9688 17 10.4688 17C6.90625 17 4 14.0938 4 10.5C4 6.9375 6.875 4 10.4688 4C14.0312 4 16.9688 6.9375 16.9688 10.5C16.9688 12.0312 16.4688 13.4375 15.5625 14.5312L19.75 18.7188ZM5.5 10.5C5.5 13.2812 7.71875 15.5 10.5 15.5C13.25 15.5 15.5 13.2812 15.5 10.5C15.5 7.75 13.25 5.5 10.5 5.5C7.71875 5.5 5.5 7.75 5.5 10.5Z"
                                                    fill="#767676"
                                                />
                                            </svg>
                                            <div className="text-13 flex gap-24 font-400 leading-4">
                                                <span>Search</span>
                                                {window?.navigator?.appVersion && (
                                                    <span>
                                                        {window?.navigator?.appVersion.indexOf(
                                                            'Win',
                                                        ) != -1
                                                            ? 'CTRL+K'
                                                            : '⌘+K'}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            }
                        />
                        <div className="flex w-full overflow-hidden h-full">
                            <Layout.Sider
                                trigger={null}
                                collapsible
                                collapsed={isSiderCollapsed}
                                className="h-full bg-white"
                                width={300}
                                collapsedWidth={60}
                            >
                                <div className="flex flex-col w-full space-y-6 h-full">
                                    <div className="">
                                        {isSiderCollapsed ? (
                                            <div className="flex flex-col items-center space-y-8">
                                                <Button
                                                    onClick={this.toggleSider}
                                                    className="p-8 cursor-pointer"
                                                    type="text"
                                                >
                                                    <svg
                                                        width="24"
                                                        height="24"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            d="M17.0156 12.5391L13.2656 16.2891C12.9844 16.5938 12.4922 16.5938 12.2109 16.2891C11.9062 16.0078 11.9062 15.5156 12.2109 15.2344L15.4219 12L12.2109 8.78906C11.9062 8.50781 11.9062 8.01562 12.2109 7.73438C12.4922 7.42969 12.9844 7.42969 13.2656 7.73438L17.0156 11.4844C17.3203 11.7656 17.3203 12.2578 17.0156 12.5391ZM8.76562 16.2891C8.48438 16.5938 7.99219 16.5938 7.71094 16.2891C7.40625 16.0078 7.40625 15.5156 7.71094 15.2344L10.9219 12L7.71094 8.78906C7.40625 8.50781 7.40625 8.01562 7.71094 7.73438C7.99219 7.42969 8.48438 7.42969 8.76562 7.73438L12.5156 11.4844C12.8203 11.7656 12.8203 12.2578 12.5156 12.5391L8.76562 16.2891Z"
                                                            fill="#484848"
                                                        />
                                                    </svg>
                                                </Button>
                                                <div className="flex flex-col items-center">
                                                    <span className="text-16 font-700">
                                                        {moment(date).format('DD')}
                                                    </span>
                                                    <span className="text-10 font-600">
                                                        {moment(date).format("MMM'YY")}
                                                    </span>
                                                </div>
                                            </div>
                                        ) : (
                                            <Collapse
                                                defaultActiveKey={['1']}
                                                // onChange={onChange}
                                                bordered={false}
                                                className="bg-white"
                                                expandIcon={({ isActive }) =>
                                                    isActive ? (
                                                        <svg
                                                            width="14"
                                                            height="8"
                                                            viewBox="0 0 14 8"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <path
                                                                d="M6.46875 0.46875C6.75 0.1875 7.21875 0.1875 7.5 0.46875L13.5312 6.46875C13.8125 6.78125 13.8125 7.25 13.5312 7.53125C13.2188 7.84375 12.75 7.84375 12.4688 7.53125L7 2.0625L1.53125 7.53125C1.21875 7.84375 0.75 7.84375 0.46875 7.53125C0.15625 7.25 0.15625 6.78125 0.46875 6.5L6.46875 0.46875Z"
                                                                fill="#484848"
                                                            />
                                                            (
                                                        </svg>
                                                    ) : (
                                                        <svg
                                                            width="14"
                                                            height="8"
                                                            viewBox="0 0 14 8"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <path
                                                                d="M6.46875 7.53125L0.46875 1.53125C0.15625 1.25 0.15625 0.78125 0.46875 0.46875C0.75 0.1875 1.21875 0.1875 1.53125 0.46875L7 5.96875L12.4688 0.5C12.75 0.1875 13.2188 0.1875 13.5312 0.5C13.8125 0.78125 13.8125 1.25 13.5312 1.53125L7.5 7.53125C7.21875 7.84375 6.75 7.84375 6.46875 7.53125Z"
                                                                fill="#484848"
                                                            />
                                                        </svg>
                                                    )
                                                }
                                                expandIconPosition="right"
                                            >
                                                <Collapse.Panel
                                                    key="1"
                                                    header={
                                                        <span className="font-700 text-14">
                                                            {getDayWord(
                                                                date.toString(),
                                                                null,
                                                                ', ',
                                                            )}
                                                            {moment(date).format("DD MMM'YY")}
                                                        </span>
                                                    }
                                                >
                                                    <Calendar
                                                        fullscreen={false}
                                                        onPanelChange={console.log}
                                                        onSelect={this.onDateChange}
                                                        headerRender={({
                                                            value,
                                                            onTypeChange,
                                                            onChange,
                                                        }) => (
                                                            <div className="flex justify-between px-16 py-8">
                                                                <span>
                                                                    {value.format('MMMM YYYY')}
                                                                </span>
                                                                <div className="flex space-x-8">
                                                                    <button
                                                                        onClick={() =>
                                                                            onChange(
                                                                                value.add(
                                                                                    -1,
                                                                                    'month',
                                                                                ),
                                                                            )
                                                                        }
                                                                        className="text-darwin-primary-500 relative"
                                                                    >
                                                                        <svg
                                                                            width="5"
                                                                            height="8"
                                                                            viewBox="0 0 5 8"
                                                                            className="w-12 h-12 fill-darwin-primary-500"
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                        >
                                                                            <path d="M0.640625 3.65625L3.64062 0.65625C3.82812 0.453125 4.15625 0.453125 4.34375 0.65625C4.54688 0.84375 4.54688 1.17188 4.34375 1.35938L1.70312 4L4.34375 6.65625C4.54688 6.84375 4.54688 7.17188 4.34375 7.35938C4.15625 7.5625 3.82812 7.5625 3.64062 7.35938L0.640625 4.35938C0.4375 4.17188 0.4375 3.84375 0.640625 3.65625Z" />
                                                                        </svg>
                                                                    </button>
                                                                    <button
                                                                        onClick={() =>
                                                                            onChange(
                                                                                value.add(
                                                                                    1,
                                                                                    'month',
                                                                                ),
                                                                            )
                                                                        }
                                                                        className="text-darwin-primary-500"
                                                                    >
                                                                        <svg
                                                                            width="24"
                                                                            height="24"
                                                                            viewBox="0 0 16 16"
                                                                            className="fill-darwin-primary-500"
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                        >
                                                                            <rect
                                                                                width="22"
                                                                                height="22"
                                                                                rx="3"
                                                                                fill="white"
                                                                            />
                                                                            <path d="M10.3438 7.65625C10.5469 7.84375 10.5469 8.17188 10.3438 8.35938L7.34375 11.3594C7.15625 11.5625 6.82812 11.5625 6.64062 11.3594C6.4375 11.1719 6.4375 10.8438 6.64062 10.6562L9.28125 8L6.64062 5.35938C6.4375 5.17188 6.4375 4.84375 6.64062 4.65625C6.82812 4.45312 7.15625 4.45312 7.34375 4.65625L10.3438 7.65625Z" />
                                                                        </svg>
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )}
                                                    />
                                                </Collapse.Panel>
                                            </Collapse>
                                        )}
                                    </div>
                                    <div className="flex justify-between space-x-8 px-16 py-8">
                                        {!isSiderCollapsed && <span>Free slots only </span>}
                                        <Switch
                                            onChange={this.toggleFreeSlotOnly}
                                            checked={isFreeSlotOnly}
                                        ></Switch>
                                    </div>
                                    <hr style={{ opacity: 0.2 }}></hr>
                                    <TableWithDragableRows
                                        showHeader={false}
                                        // id={this.TABLE_ID}
                                        tableLayout="fixed"
                                        className="mt-8 lx-reading-table h-full overflow-scroll flex flex-1"
                                        // style={{ height: 400 }}
                                        rowClassName={(t) =>
                                            `editable-row bg-darwin-primary-100 ${
                                                t.selected ? 'bg-darwin-primary-100' : ''
                                            }`
                                        }
                                        bordered
                                        dataSource={resources}
                                        columns={[
                                            {
                                                // key: 'medical-history-row-name',
                                                className: 'medical-history-row-name',
                                                render: (_: any, __: any, index: number) => {
                                                    const t = resources[index];
                                                    return {
                                                        props: {
                                                            className: t.selected
                                                                ? 'bg-darwin-primary-100'
                                                                : '',
                                                        },
                                                        children: (
                                                            <div
                                                                className={`flex space-x-8 items-center px-8 cursor-pointer hover-parent-element`}
                                                                onClick={() => {
                                                                    this.onResourceCheckChange(
                                                                        t.id,
                                                                    );
                                                                }}
                                                            >
                                                                {!isSiderCollapsed && (
                                                                    <DragHandle />
                                                                )}
                                                                <div
                                                                    className={`font-400 w-full text-neutral-black text-14 placeholder-neutral-6 items-center flex`}
                                                                >
                                                                    <div className="flex space-x-8 items-center">
                                                                        <Checkbox
                                                                            // defaultChecked={t.selected}
                                                                            checked={t.selected}
                                                                            className={
                                                                                resources.length > 1
                                                                                    ? 'hover-child-element absolute rounded-64 bg-white p-4 w-28 h-28'
                                                                                    : 'hidden'
                                                                            }
                                                                            style={{
                                                                                top: 8,
                                                                                left: 42,
                                                                            }}
                                                                            key={t.id + t.selected}
                                                                        ></Checkbox>
                                                                        <ResourceAvatar
                                                                            resource={t}
                                                                        />
                                                                        {!isSiderCollapsed && (
                                                                            <span className="truncate inline-block">
                                                                                {t.title}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ),
                                                    };
                                                },
                                            },
                                        ]}
                                        pagination={false}
                                        rowKey={(record, index) =>
                                            this.rowKeyFromId(record.id, index)
                                        }
                                        findIndexForRecordKey={this.findIndexForRecordKey}
                                        onSortEnd={this.onDrag}
                                    />
                                </div>
                            </Layout.Sider>
                            <Divider type="vertical" className="h-full relative" orientation="left">
                                {!isSiderCollapsed && (
                                    <svg
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="absolute cursor-pointer"
                                        style={{ top: 80, left: -10, zIndex: 100 }}
                                        onClick={this.toggleSider}
                                    >
                                        <rect
                                            x="0.5"
                                            y="0.5"
                                            width="23"
                                            height="23"
                                            rx="3.5"
                                            fill="#F4F4F4"
                                        />
                                        <rect
                                            x="0.5"
                                            y="0.5"
                                            width="23"
                                            height="23"
                                            rx="3.5"
                                            stroke="#D1D1D1"
                                        />
                                        <path
                                            d="M6.96094 11.4844L10.7109 7.73438C10.9922 7.42969 11.4844 7.42969 11.7656 7.73438C12.0703 8.01562 12.0703 8.50781 11.7656 8.78906L8.55469 12L11.7656 15.2344C12.0703 15.5156 12.0703 16.0078 11.7656 16.2891C11.4844 16.5938 10.9922 16.5938 10.7109 16.2891L6.96094 12.5391C6.65625 12.2578 6.65625 11.7656 6.96094 11.4844ZM15.2109 7.73438C15.4922 7.42969 15.9844 7.42969 16.2656 7.73438C16.5703 8.01562 16.5703 8.50781 16.2656 8.78906L13.0547 12L16.2656 15.2344C16.5703 15.5156 16.5703 16.0078 16.2656 16.2891C15.9844 16.5938 15.4922 16.5938 15.2109 16.2891L11.4609 12.5391C11.1562 12.2578 11.1562 11.7656 11.4609 11.4844L15.2109 7.73438Z"
                                            fill="#484848"
                                        />
                                    </svg>
                                )}
                            </Divider>
                            <div className="w-full overflow-hidden h-full">
                                <ScheduleCalendar
                                    cId={this.props.match.params.cId}
                                    resources={resources.filter((r) => r.selected)}
                                    timeView={timeView}
                                    isFreeSlotOnly={isFreeSlotOnly}
                                    date={date}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default connect()(SchedulePage);
